import { ProgressCard } from "../common/ProgressCard";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2, Info, Link as LinkIcon, PlusCircle } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { SectionHeader } from "../common/SectionHeader";
import type { PollutionApplicability, PollutionReportingReason, PollutantEntry } from "@/contexts/wizard/types";
import { cn } from "@/lib/utils";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";

const REPORTING_REASON_OPTIONS: { value: PollutionReportingReason; label: string }[] = [
  { value: "legal_requirement", label: "Legal requirement" },
  { value: "environmental_management_system", label: "Environmental Management System (EMS)" },
  { value: "voluntary_reporting", label: "Voluntary reporting" },
];

const POLLUTANT_NAMES = [
  "CO2", "NOx", "SO2", "PM10", "PM2.5", "VOC", "NH3", "CO",
  "Lead", "Cadmium", "Mercury", "Arsenic", "Benzene", "Chromium",
  "Copper", "Zinc", "Dioxins", "Furans", "PFAS", "Other",
];

const UNIT_OPTIONS = [
  { value: "kg", label: "kg" },
  { value: "tons", label: "tons" },
  { value: "g", label: "g" },
  { value: "liters", label: "liters" },
  { value: "m3", label: "m³" },
];

const MEDIUM_OPTIONS = [
  { value: "air", label: "Air" },
  { value: "water", label: "Water" },
  { value: "soil", label: "Soil" },
];

function LinkSvgIcon() {
  return <LinkIcon className="h-[17px] w-[17px] text-slate-400" strokeWidth={1.8} />;
}

const FIELD_LABEL = "text-slate-400 text-xs font-semibold uppercase leading-4 tracking-wide";
const FIELD_SELECT_TRIGGER = "h-10 rounded-2xl bg-slate-50 border-slate-200 text-sm";
const FIELD_INPUT = "h-10 rounded-2xl bg-slate-50 border-slate-200 text-sm";

export function PollutionCard({ onClose }: { onClose: () => void }) {
  const { pollutionData, updatePollutionData, pollutionCompletion, forceSave, saveStatus, isLocked, isReadOnly, assessment } = useWizard();
  const disabled = isLocked || isReadOnly;

  const reportingYear = assessment?.reportingYear
    ? String(assessment.reportingYear)
    : String(new Date().getFullYear());

  function addPollutant() {
    const existing = pollutionData.pollutants ?? [];
    const hasBlank = existing.some((e) => !e.pollutantName && e.amount === null);
    if (hasBlank) return;

    const newEntry: PollutantEntry = {
      id: crypto.randomUUID(),
      medium: "air",
      pollutantName: "",
      otherPollutantName: "",
      amount: null,
      unit: "kg",
      reportingPeriod: reportingYear,
      evidenceUrl: "",
    };
    updatePollutionData({ pollutants: [...existing, newEntry] });
  }

  function updatePollutant(id: string, changes: Partial<PollutantEntry>) {
    updatePollutionData({
      pollutants: pollutionData.pollutants?.map((e) => (e.id === id ? { ...e, ...changes } : e)),
    });
  }

  function removePollutant(id: string) {
    updatePollutionData({ pollutants: pollutionData.pollutants?.filter((e) => e.id !== id) });
  }

  const showYesBranch = pollutionData.applicability === "yes";
  const showPollutantList = showYesBranch && !!pollutionData.reportingReason;
  const reasonMissing = showYesBranch && !pollutionData.reportingReason;

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Pollution of Air, Water and Soil (B4)"}
        description={"Does your business release any pollutants to air, water, or soil? Applies only if legally required or voluntarily tracked"}
        onClose={onClose}
        onSave={forceSave}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Environment"}
      />

      {disabled && <LockedBanner />}

      {/* Section A — Applicability (VSME 2.2.1.1) */}
      <SectionCard header={<SectionHeader icon={<Info className="h-[18px] w-[18px] shrink-0 text-slate-500" strokeWidth={1.8} />} title={"Pollution Reporting Applicability"} />}>
        <p className="text-slate-500 text-sm font-normal leading-5">{"Are you required by law, or do you voluntarily report pollution emissions/releases to a government authority or environmental body?"}</p>
        <RadioGroup
          value={pollutionData.applicability ?? ""}
          onValueChange={(value: PollutionApplicability) => {
            updatePollutionData({
              applicability: value,
              ...(value === "no" ? { reportingReason: null, pollutants: [] } : {}),
            });
          }}
          disabled={disabled}
          className="flex gap-4 pt-2"
        >
          {(["yes", "no"] as PollutionApplicability[]).map((val) => {
            const selected = pollutionData.applicability === val;
            return (
              <Label
                key={val}
                htmlFor={`applicability_${val}`}
                className={cn(
                  "flex-1 flex items-center gap-2.5 pl-3.5 py-4 pr-4 rounded-2xl transition-colors",
                  disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                  selected
                    ? "bg-primary/5 outline outline-1 outline-offset-[-1px] outline-primary/20"
                    : "outline outline-1 outline-offset-[-1px] outline-slate-200"
                )}
              >
                {/* Hidden accessible radio input */}
                <RadioGroupItem value={val} id={`applicability_${val}`} className="sr-only" />
                {/* Custom visual indicator */}
                <div
                  className={cn(
                    "w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center",
                    selected ? "bg-primary" : "border border-gray-400 bg-white"
                  )}
                >
                  {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <span className="text-slate-900 text-sm font-medium">
                  {val === "yes" ? "Yes" : "No"}
                </span>
              </Label>
            );
          })}
        </RadioGroup>
      </SectionCard>

      {/* Section B — Reason for reporting (VSME 2.2.1.2) */}
      {showYesBranch && (
        <SectionCard header={<SectionHeader icon={<Info className="h-[18px] w-[18px] shrink-0 text-slate-500" strokeWidth={1.8} />} title={"Reason for Reporting"} />}>
          <div className="max-w-sm flex flex-col gap-2">
            <Label className={FIELD_LABEL}>
              {"Why do you report pollution emissions/releases?"}
            </Label>
            <Select
              value={pollutionData.reportingReason ?? ""}
              onValueChange={(value: PollutionReportingReason) =>
                updatePollutionData({ reportingReason: value })
              }
              disabled={disabled}
            >
              <SelectTrigger id="reporting_reason" className={FIELD_SELECT_TRIGGER}>
                <SelectValue placeholder={"Select reason..."} />
              </SelectTrigger>
              <SelectContent>
                {REPORTING_REASON_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {reasonMissing && (
              <p className="text-xs text-destructive font-medium">
                {"Reason is required when pollution reporting is applicable."}
              </p>
            )}
          </div>
        </SectionCard>
      )}

      {/* Section C — Short note + published data link (VSME 2.2.1.3 / 2.2.1.4) */}
      {showYesBranch && (
        <SectionCard header={<SectionHeader icon={<Info className="h-[18px] w-[18px] shrink-0 text-slate-500" strokeWidth={1.8} />} title={"Additional Information"} />}>
          <div className="flex flex-col gap-2">
            <Label className={FIELD_LABEL}>
              {"Short note"}{" "}
              <span className="text-muted-foreground font-normal normal-case">{"(optional)"}</span>
            </Label>
            <Textarea
              id="pollution_notes"
              value={pollutionData.notes}
              onChange={(e) => updatePollutionData({ notes: e.target.value })}
              placeholder={"Any additional context about your pollution reporting..."}
              disabled={disabled}
              rows={4}
              className="rounded-2xl bg-slate-50 border-slate-200 text-sm resize-none"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label className={FIELD_LABEL}>
              {"Link to published data"}{" "}
              <span className="text-muted-foreground font-normal normal-case">{"(optional)"}</span>
            </Label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <LinkSvgIcon />
              </div>
              <Input
                id="published_data_link"
                type="url"
                value={pollutionData.publishedDataLink}
                onChange={(e) => updatePollutionData({ publishedDataLink: e.target.value })}
                placeholder="https://..."
                disabled={disabled}
                className={cn(FIELD_INPUT, "pl-10")}
              />
            </div>
            <p className="text-slate-400 text-[10px] font-normal leading-4">
              {"Enter only relevant data."}
            </p>
          </div>
        </SectionCard>
      )}

      {/* Section D — Pollutant entries (only when Yes + Reason selected) */}
      {showPollutantList && (
        <SectionCard
          header={
            <div className="flex justify-between items-center">
              <h4 className="text-slate-800 text-lg font-bold leading-7">{"Pollutant Entries"}</h4>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); addPollutant(); }}
                  className="flex items-center gap-2 text-primary text-sm font-bold hover:text-primary/80 transition-colors"
                >
                  <PlusCircle className="h-[17px] w-[17px] text-primary" strokeWidth={1.8} />
                  {"Add Pollutant"}
                </button>
              )}
            </div>
          }
        >
          {pollutionData.pollutants?.length === 0 ? (
            <p className="text-sm text-muted-foreground">{"No pollutants added yet. Click \"Add Pollutant\" to record a release."}</p>
          ) : (
            <div className="flex flex-col gap-4">
              {pollutionData.pollutants?.map((entry, index) => (
                <div
                  key={entry.id}
                  className="wizard-card wizard-card-entry flex flex-col"
                >
                  {/* Entry header row */}
                  <div className="sticky top-0 z-10 px-6 py-3 bg-primary/5 border-b border-slate-200 flex justify-between items-center">
                    <span className="text-slate-500 text-xs font-bold uppercase leading-4">
                      {`Entry ${index + 1}`}
                    </span>
                    {!disabled && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removePollutant(entry.id)}
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Entry fields */}
                  <div className="p-6 flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Medium */}
                      <div className="flex flex-col gap-2">
                        <Label className={FIELD_LABEL}>{"Medium"}</Label>
                        <Select
                          value={entry.medium}
                          onValueChange={(v) => updatePollutant(entry.id, { medium: v as PollutantEntry["medium"] })}
                          disabled={disabled}
                        >
                          <SelectTrigger className={FIELD_SELECT_TRIGGER}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MEDIUM_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Pollutant name */}
                      <div className="flex flex-col gap-2">
                        <Label className={FIELD_LABEL}>Pollutant</Label>
                        <Select
                          value={entry.pollutantName}
                          onValueChange={(v) => updatePollutant(entry.id, { pollutantName: v, otherPollutantName: v === "Other" ? (entry.otherPollutantName ?? "") : "" })}
                          disabled={disabled}
                        >
                          <SelectTrigger className={FIELD_SELECT_TRIGGER}>
                            <SelectValue placeholder="Select a pollutant" />
                          </SelectTrigger>
                          <SelectContent>
                            {POLLUTANT_NAMES.map((name) => (
                              <SelectItem key={name} value={name}>{name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {entry.pollutantName === "Other" && (
                          <Input
                            value={entry.otherPollutantName ?? ""}
                            onChange={(e) => updatePollutant(entry.id, { otherPollutantName: e.target.value })}
                            disabled={disabled}
                            className={FIELD_INPUT}
                            placeholder="Specify pollutant name"
                          />
                        )}
                      </div>

                      {/* Amount */}
                      <div className="flex flex-col gap-2">
                        <Label className={FIELD_LABEL}>{"Amount"}</Label>
                        <Input
                          type="number"
                          min={0}
                          value={entry.amount ?? ""}
                          onChange={(e) =>
                            updatePollutant(entry.id, {
                              amount: e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          disabled={disabled}
                          className={FIELD_INPUT}
                          placeholder="0"
                        />
                      </div>

                      {/* Unit */}
                      <div className="flex flex-col gap-2">
                        <Label className={FIELD_LABEL}>{"Unit"}</Label>
                        <Select
                          value={entry.unit}
                          onValueChange={(v) => updatePollutant(entry.id, { unit: v as PollutantEntry["unit"] })}
                          disabled={disabled}
                        >
                          <SelectTrigger className={FIELD_SELECT_TRIGGER}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Reporting period (display only) */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {"Reporting period"}: <strong>{entry.reportingPeriod}</strong>
                      </span>
                    </div>

                    {/* Evidence URL */}
                    <div className="flex flex-col gap-2">
                      <Label className={FIELD_LABEL}>
                        {"Evidence"}{" "}
                        <span className="text-muted-foreground font-normal normal-case">
                          {"(optional URL)"}
                        </span>
                      </Label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <LinkSvgIcon />
                        </div>
                        <Input
                          type="url"
                          value={entry.evidenceUrl ?? ""}
                          onChange={(e) => updatePollutant(entry.id, { evidenceUrl: e.target.value })}
                          disabled={disabled}
                          className={cn(FIELD_INPUT, "pl-10")}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      <TransitionMeasuresBlock topicId="pollution" currentTab="environment" disabled={disabled} />

      <ProgressCard completion={pollutionCompletion} />
    </div>
  );
}
